import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import { useMutation, useQueryClient } from 'react-query'
import createGroup from '../../../../Apis/groups/createGroup'
import updateGroup from '../../../../Apis/groups/updateGroup'
import QueryKeys from '../../../../utils/constants/queryKeys'
import editGroupModalStyles from './editGroupModal.styles'
import { GROUP_TYPES_TO_LECTICAL } from '../../../../utils/constants/groupTypes'

const useStyles = makeStyles(editGroupModalStyles)

const EditGroupModal = (props) => {
  const { isOpen, onClose, groupData, orgId, seasonId, stageId } = props
  const isEditMode = Boolean(groupData)
  const classes = useStyles()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    name: '',
    type: 'RoundRobin',
  })

  useEffect(() => {
    if (groupData) {
      setFormData({
        name: groupData.name || '',
        type: groupData.type || 'RoundRobin',
      })
    } else {
      setFormData({ name: '', type: 'RoundRobin' })
    }
  }, [groupData, isOpen])

  const createGroupMutation = useMutation(
    (data) => createGroup({ orgId, seasonId, data }),
    {
      onSuccess: () => {
        // Invalidate stages query to refresh groups
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        onClose()
      },
    }
  )

  const updateGroupMutation = useMutation(
    ({ groupId, data }) => updateGroup({ orgId, seasonId, groupId, data }),
    {
      onSuccess: () => {
        // Invalidate stages query to refresh groups
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        onClose()
      },
    }
  )

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value })
  }

  const handleSubmit = () => {
    const payload = {
      name: formData.name,
      type: formData.type,
    }

    if (isEditMode) {
      updateGroupMutation.mutate({
        groupId: groupData.id,
        data: payload,
      })
    } else {
      payload.stageId = stageId
      createGroupMutation.mutate(payload)
    }
  }

  const isLoading = createGroupMutation.isLoading || updateGroupMutation.isLoading

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit Group' : 'Create New Group'}
      </DialogTitle>
      <DialogContent>
        <TextField
          label='Group Name'
          fullWidth
          value={formData.name}
          onChange={handleInputChange('name')}
          className={classes.dialogField}
          margin='normal'
          required
        />
        <TextField
          label='Type'
          fullWidth
          select
          value={formData.type}
          onChange={handleInputChange('type')}
          className={classes.dialogField}
          margin='normal'
        >
          {GROUP_TYPES_TO_LECTICAL.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color='primary'
          variant='contained'
          disabled={isLoading || !formData.name.trim()}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : isEditMode ? (
            'Update'
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

EditGroupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  groupData: PropTypes.object,
  orgId: PropTypes.string.isRequired,
  seasonId: PropTypes.string.isRequired,
  stageId: PropTypes.string,
}

export default EditGroupModal
